import PropTypes from "prop-types";
import React from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { PRETTY_MAX_FILE_SIZE, VALID_IMAGE_EXTENSIONS } from "@/utils/fileUtil";
import { testLocator } from "@/lib/helpers/test-locator";

import { DateInput } from "../../DateInput";
import { Dropdown } from "../../Dropdown";
import { FormField } from "../../FormField";
import { Input } from "../../Input";
import { UploadFile } from "../../UploadFile";

import "./styles.scss";

const uploadFileInfoContent = `Please upload images of the front and back of your insurance card. Acceptable file formats: ${VALID_IMAGE_EXTENSIONS.join(
  ", ",
)}. Max file size: ${PRETTY_MAX_FILE_SIZE}`;

export function EditProfileInsurance({
  errors,
  files,
  groupIdTooltipContents,
  handleBlur,
  handleChange,
  handleFocus,
  insuranceCompaniesOptions,
  insuranceCompanyFilterOptions,
  memberIdPlaceholder,
  memberIdTip,
  memberIdTooltipContents,
  noInsurance,
  onBrowseButtonClicked,
  onCancelButtonClick,
  onFileDelete,
  onSubmitButtonClick,
  policyHolderOptions,
  stateOfInsurancePlanOptions,
  title,
  touched,
  values,
  ...props
}) {
  let insuranceDisabledClassName = "";

  if (noInsurance) {
    insuranceDisabledClassName = "apti-disabled";
  }

  return (
    <DashboardCard2 title={title} {...props}>
      <div className="apti-EditProfileInsurance--grid">
        <FormField
          className="apti-EditProfileInsurance--field"
          errorMessage={touched.insuranceCompany ? errors.insuranceCompany : ""}
          label="Insurance Company"
          sublabel="(Optional)"
        >
          <Dropdown
            error={touched.insuranceCompany && errors.insuranceCompany}
            filterOptions={insuranceCompanyFilterOptions}
            id="insuranceCarrierDropdown"
            name="insuranceCompany"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            options={insuranceCompaniesOptions}
            placeholder="Select Insurance Company"
            value={values.insuranceCompany}
          />
        </FormField>

        <FormField
          className={cn("apti-EditProfileInsurance--field", insuranceDisabledClassName)}
          errorMessage={touched.policyHolder ? errors.policyHolder : ""}
          label="Policy Holder"
        >
          <Dropdown
            className={insuranceDisabledClassName}
            error={touched.policyHolder && errors.policyHolder}
            isDisabled={noInsurance}
            name="policyHolder"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            options={policyHolderOptions}
            placeholder="Select Policy Holder"
            value={values.policyHolder}
          />
        </FormField>

        <FormField
          className={cn("apti-EditProfileInsurance--field", insuranceDisabledClassName)}
          errorMessage={touched.memberId ? errors.memberId : ""}
          label="Member ID"
        >
          {memberIdTip && <div className="txt-gry mb-2">{memberIdTip}</div>}
          <Input
            className={insuranceDisabledClassName}
            disabled={noInsurance}
            error={touched.memberId && errors.memberId}
            infoContent={memberIdTooltipContents}
            name="memberId"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder={memberIdPlaceholder}
            value={values.memberId}
            {...testLocator("insuranceMemberIdInput")}
          />
        </FormField>

        <FormField
          className={cn("apti-EditProfileInsurance--field", insuranceDisabledClassName)}
          errorMessage={touched.groupId ? errors.groupId : ""}
          label="Group ID"
          sublabel="(Optional)"
        >
          <Input
            className={insuranceDisabledClassName}
            disabled={noInsurance}
            error={touched.groupId && errors.groupId}
            infoContent={groupIdTooltipContents}
            name="groupId"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="Enter Group ID"
            value={values.groupId}
          />
        </FormField>

        <UploadFile
          className={cn("apti-EditProfileInsurance--upload", insuranceDisabledClassName)}
          disabled={noInsurance}
          files={files}
          infoContent={uploadFileInfoContent}
          onBrowseButtonClicked={onBrowseButtonClicked}
          onFileDelete={onFileDelete}
          testId="insurance-browse-button"
        />

        <FormField
          className={cn("apti-EditProfileInsurance--field", insuranceDisabledClassName)}
          errorMessage={touched.stateOfInsurance ? errors.stateOfInsurance : ""}
          label="State of Insurance Plan"
          sublabel="(Optional)"
        >
          <Dropdown
            className={insuranceDisabledClassName}
            error={touched.stateOfInsurance && errors.stateOfInsurance}
            isDisabled={noInsurance}
            name="stateOfInsurance"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            options={stateOfInsurancePlanOptions}
            placeholder="Select State"
            value={values.stateOfInsurance}
          />
        </FormField>

        <FormField
          className={cn("apti-EditProfileInsurance--field", insuranceDisabledClassName)}
          errorMessage={touched.policyHolderName ? errors.policyHolderName : ""}
          label="Policy Holder Name"
          sublabel="(Optional)"
        >
          <Input
            className={insuranceDisabledClassName}
            disabled={noInsurance}
            error={touched.policyHolderName && errors.policyHolderName}
            name="policyHolderName"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="Enter Policy Holder Name"
            value={values.policyHolderName}
          />
        </FormField>

        <FormField
          className={cn("apti-EditProfileInsurance--field", insuranceDisabledClassName)}
          errorMessage={touched.policyHolderDOB ? errors.policyHolderDOB : ""}
          label="Policy Holder Date of Birth"
          sublabel="(Optional)"
        >
          <DateInput
            className={insuranceDisabledClassName}
            disabled={noInsurance}
            error={touched.policyHolderDOB && errors.policyHolderDOB}
            name="policyHolderDOB"
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            placeholder="MM/DD/YYYY"
            value={values.policyHolderDOB}
          />
        </FormField>
      </div>

      <div className="tw:mt-5 tw:flex tw:gap-5 tw:place-self-end">
        <Button2 className="tw:min-w-40" onPress={onCancelButtonClick} variant="secondary">
          Cancel
        </Button2>

        <Button2 className="tw:min-w-40" onPress={onSubmitButtonClick} type="submit">
          Save
        </Button2>
      </div>
    </DashboardCard2>
  );
}

EditProfileInsurance.propTypes = {
  className: PropTypes.string,
  errors: PropTypes.object.isRequired,

  files: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  insuranceCompaniesOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  noInsurance: PropTypes.bool,

  onBrowseButtonClicked: PropTypes.func.isRequired,
  onCancelButtonClick: PropTypes.func.isRequired,
  onFileDelete: PropTypes.func.isRequired,

  onSubmitButtonClick: PropTypes.func.isRequired,
  policyHolderOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  stateOfInsurancePlanOptions: PropTypes.arrayOf(PropTypes.object).isRequired,

  title: PropTypes.string.isRequired,
  touched: PropTypes.object.isRequired,

  values: PropTypes.object.isRequired,
};

EditProfileInsurance.defaultProps = {
  className: undefined,
  errors: {},

  files: [],
  handleBlur: () => {},
  handleChange: () => {},
  handleFocus: () => {},
  insuranceCompaniesOptions: [],
  noInsurance: false,

  onBrowseButtonClicked: () => {},
  onCancelButtonClick: () => {},
  onFileDelete: () => {},

  onSubmitButtonClick: () => {},
  policyHolderOptions: [],
  stateOfInsurancePlanOptions: [],

  title: "",
  touched: {},

  values: {},
};
